import React, { useEffect, useState, useMemo, useRef, useLayoutEffect } from 'react';
import { LEAGUES, MARKETS, SPORTS, SPORTSBOOKS, getWagerWizDisplayOptions, convertWagerWizSelectionToBackend, convertBackendToWagerWizDisplay } from '../../utils/constants';
import { LEAGUE_SPORT_MAP } from '../../pages/screener/leagueToSportMapping';
import checked_checkbox from '../../assets/images/checked_checkbox.svg';
import unchecked_checkbox from '../../assets/images/unchecked_checkbox.svg';
import magnifying_glass from '../../assets/images/magnifying_glass.svg';
import { Modal, Popover, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material';
import axios from 'axios';
import { CustomAutoCompleteFilter } from '../evscreen/Filters/CustomAutocompleteFilter';
import { IOSSwitch } from '../../components/IOSSwitch';
import { MinMaxEvComponent } from '../evscreen/Filters/MinMaxEv';
import { DatePickerComponent } from '../evscreen/Filters/Datepicker';
import edit_icon from '../../assets/images/edit_icon.svg';
import play_icon from '../../assets/images/play_icon.svg';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { createPortal } from 'react-dom';
import { saveUserInfoOutliers } from '../../utils/api/saveUserInfo';
import EditIcon from '@mui/icons-material/Edit';
import { LogoGenerator } from '../../components/LogoGenerator';
import { getSportsbookDisplayName } from '../../utils/sportsbookAliases';

const propertyToTitleMapping = {
	sportsbooks: 'Sportsbooks',
	sharpBooks: 'Sharp Books',
	sports: 'Sports',
	leagues: 'Leagues',
	markets: 'Markets',
	marketTypes: 'Market Types',
	minMaxEv: 'Min/Max Discrepancy',
	minMaxOdds: 'Min/Max Odds',
	limit: 'Sharp Book Limit',
	hide_alternates: 'Hide Alternate Lines',
	date: 'Date Range',
	name: 'Filter Name',
	marketDepth: 'Market Depth',
};

// Helper function to categorize markets by Type and Time (same as EV tool)
const categorizeMarkets = (markets) => {
	const categorized = {};
	const seen = new Set();
	
	markets.forEach(market => {
		if (seen.has(market)) return;
		seen.add(market);
		
		let type = 'Other';
		const marketLower = market.toLowerCase();
		
		if (marketLower.includes('player') || marketLower.includes('fantasy score') || marketLower.includes('scorer') || 
			marketLower.includes('batsman') || marketLower.includes('bowler') || marketLower.includes('man of the match') ||
			marketLower.includes('first goal scorer') || marketLower.includes('last goal scorer') || 
			marketLower.includes('second goal scorer') || marketLower.includes('third goal scorer') ||
			marketLower.includes('first touchdown scorer') || marketLower.includes('last touchdown scorer') ||
			marketLower.includes('first basket') || marketLower.includes('first try scorer') ||
			marketLower.includes('first card receiver') || marketLower.includes('anytime card receiver') ||
			marketLower.includes('anytime red card receiver') || marketLower.includes('first card') ||
			marketLower.includes('player assists') || marketLower.includes('player goals') ||
			marketLower.includes('player points') || marketLower.includes('player rebounds') ||
			marketLower.includes('player saves') || marketLower.includes('player shots') ||
			marketLower.includes('player passes') || marketLower.includes('player tackles') ||
			marketLower.includes('player blocks') || marketLower.includes('player steals') ||
			marketLower.includes('player runs') || marketLower.includes('player hits') ||
			marketLower.includes('player strikeouts') || marketLower.includes('player aces') ||
			marketLower.includes('player cards') || marketLower.includes('player fouls') ||
			marketLower.includes('player interceptions') || marketLower.includes('player sacks') ||
			marketLower.includes('player touchdowns') || marketLower.includes('player yards') ||
			marketLower.includes('player fantasy')) {
			type = 'Player Prop';
		} else if (marketLower.includes('moneyline') || marketLower.includes('draw no bet')) {
			type = 'Moneyline';
		} else if (marketLower.includes('spread') || marketLower.includes('handicap') || marketLower.includes('puck line') || 
			marketLower.includes('run line') || marketLower.includes('point spread') || marketLower.includes('goal spread') ||
			marketLower.includes('asian handicap') || marketLower.includes('game spread') || 
			marketLower.includes('set handicap') || marketLower.includes('round handicap') ||
			marketLower.includes('map handicap') || marketLower.includes('kills handicap') ||
			marketLower.includes('aces handicap') || marketLower.includes('cards handicap') ||
			marketLower.includes('corners handicap')) {
			type = 'Spread';
		} else if (marketLower.includes('total') || marketLower.includes('over/under') || marketLower.includes('odd/even')) {
			type = 'Total';
		} else if (marketLower.includes('team total') || marketLower.includes('team to score') || 
			marketLower.includes('team to win') || marketLower.includes('team clean sheet') ||
			marketLower.includes('first team to score') || marketLower.includes('last team to score') ||
			marketLower.includes('team highest') || marketLower.includes('team with most') ||
			marketLower.includes('team top')) {
			type = 'Team';
		} else if (marketLower.includes('race to')) {
			type = 'Race To';
		} else {
			type = 'Other';
		}
		
		let time = 'Game';
		
		if (marketLower.includes('inning') || marketLower.includes('innings')) {
			time = 'Inning';
		} else if (/[0-9]+(st|nd|rd|th)\s+set/i.test(marketLower) || 
			(marketLower.includes('set') && !marketLower.includes('handicap') && !marketLower.includes('set handicap') &&
			 (marketLower.includes('1st set') || marketLower.includes('2nd set') || 
			  marketLower.includes('3rd set') || marketLower.includes('4th set') || 
			  marketLower.includes('5th set')))) {
			time = 'Set';
		} else if (/[0-9]+(st|nd|rd|th)\s+([0-9]+\s+)?maps?/i.test(marketLower) || 
			(marketLower.includes('map') && !marketLower.includes('handicap') && !marketLower.includes('map handicap') &&
			 (marketLower.includes('1st map') || marketLower.includes('2nd map') || 
			  marketLower.includes('3rd map') || marketLower.includes('4th map') || 
			  marketLower.includes('5th map') || marketLower.includes('1st 2 maps') ||
			  marketLower.includes('1st 3 maps') || marketLower.includes('1st 4 maps') ||
			  marketLower.includes('maps player') || /[0-9]+(st|nd|rd|th)\s+[0-9]+\s+maps/i.test(marketLower)))) {
			time = 'Map';
		} else if (marketLower.includes('1st half') || marketLower.includes('1st quarter') || 
			marketLower.includes('1st period') ||
			marketLower.includes('1st 10 minutes') || marketLower.includes('1st 5 minutes') ||
			marketLower.includes('1st 3 quarters') ||
			marketLower.includes('1st round') || marketLower.includes('1st 3 holes') ||
			marketLower.includes('1st x overs')) {
			if (marketLower.includes('1st half')) time = '1st Half';
			else if (marketLower.includes('1st quarter')) time = '1st Quarter';
			else if (marketLower.includes('1st period')) time = '1st Period';
			else if (marketLower.includes('1st 10 minutes')) time = '1st 10 Mins';
			else if (marketLower.includes('1st 5 minutes')) time = '1st 5 Mins';
			else if (marketLower.includes('1st 3 quarters')) time = '1st 3 Quarter';
			else if (marketLower.includes('1st round')) time = '1st Round';
			else if (marketLower.includes('1st 3 holes')) time = '1st 3 Holes';
			else if (marketLower.includes('1st x overs')) time = '1st X Overs';
			else time = '1st Half';
		} else if (marketLower.includes('2nd half') || marketLower.includes('2nd quarter') || 
			marketLower.includes('2nd period') ||
			marketLower.includes('2nd round')) {
			if (marketLower.includes('2nd half')) time = '2nd Half';
			else if (marketLower.includes('2nd quarter')) time = '2nd Quarter';
			else if (marketLower.includes('2nd period')) time = '2nd Period';
			else if (marketLower.includes('2nd round')) time = '2nd Round';
			else time = '2nd Half';
		} else if (marketLower.includes('3rd quarter') || marketLower.includes('3rd period')) {
			if (marketLower.includes('3rd quarter')) time = '3rd Quarter';
			else if (marketLower.includes('3rd period')) time = '3rd Period';
			else time = '3rd Quarter';
		} else if (marketLower.includes('4th quarter')) {
			time = '4th Quarter';
		} else if (marketLower.includes('reg time') || marketLower.includes('no ot') || marketLower.includes('(no ot)')) {
			time = 'Game';
		} else if (marketLower.includes('single over') || marketLower.includes('1st over') || 
			marketLower.includes('2nd over') || marketLower.includes('over team total')) {
			time = 'Single Over';
		} else if (marketLower.includes('1st 3 quarter')) {
			time = '1st 3 Quarter';
		} else if (marketLower.includes('golf')) {
			time = 'Golf';
		} else {
			time = 'Game';
		}
		
		if (!categorized[type]) {
			categorized[type] = {};
		}
		if (!categorized[type][time]) {
			categorized[type][time] = [];
		}
		categorized[type][time].push({ label: market, value: market });
	});
	
	return categorized;
};

// Helper function for sportsbook equivalence
function isSportsbookEquivalent(a, b) {
	const map = {
		'ww_mains': 'WagerWiz Mains',
		'WagerWiz Mains': 'ww_mains',
		'ww_props': 'WagerWiz Props',
		'WagerWiz Props': 'ww_props',
		'WagerWiz': 'WagerWiz Mains',
		'Pinnacle': 'bet105',
		'bet105': 'Pinnacle',
	};
	return a === b || map[a] === b || map[b] === a;
}

// Hide Alternates Filter Content Component
const HideAlternatesContent = ({ filters, activeRowIdx, setFilters, email, onClose }) => {
	const isChecked = !!filters[activeRowIdx]?.hide_alternates;

	const handleToggle = async () => {
		const copy = JSON.parse(JSON.stringify(filters));
		copy[activeRowIdx].hide_alternates = !isChecked;
		setFilters(copy);
		const userdb = JSON.parse(window.localStorage.getItem('userdb'));
		const selectedName = userdb.selected_query || (userdb.queries?.[0]?.[0]?.name ?? 'default');
		const idx = userdb.queries.findIndex(q => q?.[0]?.name === selectedName);
		if (idx !== -1) {
			userdb.queries[idx] = copy;
			window.localStorage.setItem('userdb', JSON.stringify(userdb));
			await saveUserInfoOutliers(copy, email);
		}
	};

	return createPortal(
		<div style={{zIndex: 10000}} className="bg-gray-900 text-white fixed top-0 left-0 w-full h-full p-4 flex flex-col">
			<div className="flex items-center justify-between mb-6">
				<div className="flex items-center gap-3">
					{onClose && (
						<button
							className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-800/80 border border-gray-600 hover:bg-gray-700 hover:border-gray-500 transition-all duration-200 backdrop-blur-sm"
							aria-label="Back"
							onClick={onClose}
						>
							<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
							</svg>
						</button>
					)}
					<div>
						<h2 className="text-xl font-bold text-white">Hide Alternate Lines</h2>
					</div>
				</div>
				<button 
					onClick={onClose} 
					className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-800/80 border border-gray-600 hover:bg-gray-700 hover:border-gray-500 transition-all duration-200 backdrop-blur-sm"
					aria-label="Close"
				>
					<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
					</svg>
				</button>
			</div>
			
			<div className="flex-1 overflow-y-auto flex items-center justify-center">
				<div className="w-full max-w-md space-y-6">
					<div className="space-y-3 text-center">
						<p className="text-base text-gray-400">
							Hide alternate betting lines and only show primary/featured lines.
						</p>
					</div>
					
					<div className="flex flex-col items-center gap-4">
						<button
							onClick={handleToggle}
							className={`relative inline-flex items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${
								isChecked ? 'bg-blue-600' : 'bg-gray-700'
							}`}
							style={{
								width: '72px',
								height: '40px',
							}}
							role="switch"
							aria-checked={isChecked}
							aria-label="Hide Alternate Lines"
						>
							<span
								className={`inline-block rounded-full bg-white shadow-lg transform transition-transform duration-300 ${
									isChecked ? 'translate-x-8' : 'translate-x-1'
								}`}
								style={{
									width: '32px',
									height: '32px',
								}}
							/>
						</button>
						
						<div className="flex items-center gap-3">
							<span className={`text-lg font-semibold ${isChecked ? 'text-blue-400' : 'text-gray-400'}`}>
								{isChecked ? 'Enabled' : 'Disabled'}
							</span>
						</div>
					</div>
					
					<div className="mt-8 pt-6 border-t border-gray-700">
						<p className="text-sm text-gray-500 leading-relaxed text-center">
							Alternate lines include spreads, totals, or prop variations that differ from the most likely outcomes. Hiding alt lines can help you focus on more liquid markets and delay being profiled as a winning player.
						</p>
					</div>
				</div>
			</div>
		</div>,
		document.body
	);
};

// Limit Filter Content Component - Integrated keypad without modal overlay
const LimitFilterContent = ({ filters, activeRowIdx, setFilters, email, onClose }) => {
	const [input, setInput] = useState((filters[activeRowIdx]?.limit || 0).toString());

	useEffect(() => {
		setInput((filters[activeRowIdx]?.limit || 0).toString());
	}, [filters, activeRowIdx]);

	const handleKey = (num) => {
		if (input.length >= 7) return;
		if (input === '0') {
			setInput(num);
		} else {
			setInput(input + num);
		}
	};

	const handleBackspace = () => {
		if (input.length === 1) {
			setInput('0');
		} else {
			setInput(input.slice(0, -1));
		}
	};

	const handleSubmit = async () => {
		const val = parseInt(input, 10);
		if (!isNaN(val) && val >= 0) {
			const copy = JSON.parse(JSON.stringify(filters));
			copy[activeRowIdx].limit = val;
			setFilters(copy);
			const userdb = JSON.parse(window.localStorage.getItem('userdb'));
			const selectedName = userdb.selected_query || (userdb.queries?.[0]?.[0]?.name ?? 'default');
			const idx = userdb.queries.findIndex(q => q?.[0]?.name === selectedName);
			if (idx !== -1) {
				userdb.queries[idx] = copy;
				window.localStorage.setItem('userdb', JSON.stringify(userdb));
				await saveUserInfoOutliers(copy, email);
			}
		}
	};

	return createPortal(
		<div style={{zIndex: 10000}} className="bg-gray-900 text-white fixed top-0 left-0 w-full h-full p-4 flex flex-col">
			<div className="flex items-center justify-between mb-6">
				<div className="flex items-center gap-3">
					{onClose && (
						<button
							className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-800/80 border border-gray-600 hover:bg-gray-700 hover:border-gray-500 transition-all duration-200 backdrop-blur-sm"
							aria-label="Back"
							onClick={onClose}
						>
							<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
							</svg>
						</button>
					)}
					<div>
						<h2 className="text-xl font-bold text-white">Sharp Book Limit</h2>
					</div>
				</div>
				<button 
					onClick={onClose} 
					className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-800/80 border border-gray-600 hover:bg-gray-700 hover:border-gray-500 transition-all duration-200 backdrop-blur-sm"
					aria-label="Close"
				>
					<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
					</svg>
				</button>
			</div>
			
			<div className="flex-1 overflow-y-auto space-y-4">
				<div className="space-y-3">
					<p className="text-base text-gray-400">
						Where available, filter for plays where sharp books are accepting bet sizes over:
					</p>
					
					<div className="flex items-center justify-center py-4">
						<span className="text-4xl font-bold text-gray-300">$</span>
						<span className="text-5xl font-bold ml-2 text-white">
							{parseInt(input).toLocaleString()}
						</span>
					</div>
					
					<div className="w-full max-w-xs mx-auto">
						<div className="grid grid-cols-3 gap-3 mb-4">
							{[1,2,3,4,5,6,7,8,9].map(n => (
								<button
									key={n}
									className="bg-gray-800 text-white text-2xl rounded-xl py-4 hover:bg-blue-700 active:bg-blue-700 focus:outline-none focus:ring-0 focus:bg-gray-800 transition-colors font-semibold touch-manipulation"
									onClick={() => handleKey(n.toString())}
									onTouchEnd={(e) => {
										e.currentTarget.blur();
									}}
									onMouseUp={(e) => {
										e.currentTarget.blur();
									}}
								>
									{n}
								</button>
							))}
							<div></div>
							<button
								className="bg-gray-800 text-white text-2xl rounded-xl py-4 hover:bg-blue-700 active:bg-blue-700 focus:outline-none focus:ring-0 focus:bg-gray-800 transition-colors font-semibold touch-manipulation"
								onClick={() => handleKey('0')}
								onTouchEnd={(e) => {
									e.currentTarget.blur();
								}}
								onMouseUp={(e) => {
									e.currentTarget.blur();
								}}
							>
								0
							</button>
							<button
								className="bg-gray-800 text-white text-2xl rounded-xl py-4 hover:bg-blue-700 active:bg-blue-700 focus:outline-none focus:ring-0 focus:bg-gray-800 transition-colors font-semibold touch-manipulation"
								onClick={handleBackspace}
								onTouchEnd={(e) => {
									e.currentTarget.blur();
								}}
								onMouseUp={(e) => {
									e.currentTarget.blur();
								}}
							>
								←
							</button>
							<button
								className="bg-blue-600 text-white text-xl rounded-xl py-4 hover:bg-blue-700 active:bg-blue-700 focus:outline-none focus:ring-0 focus:bg-blue-600 transition-colors col-span-3 font-semibold touch-manipulation"
								onClick={handleSubmit}
								onTouchEnd={(e) => {
									e.currentTarget.blur();
								}}
								onMouseUp={(e) => {
									e.currentTarget.blur();
								}}
							>
								Save
							</button>
						</div>
					</div>
					
					<div className="space-y-3">
						<p className="text-sm text-gray-500 leading-relaxed">
							Sharp books allow larger bet sizes when they are more confident on a market. Setting a minimum sharp book limit helps filter out plays sharp books are less confident about.
						</p>
						<p className="text-sm text-gray-500 leading-relaxed">
							For beginners, we recommend setting this filter to $2,000+.
						</p>
					</div>
				</div>
			</div>
		</div>,
		document.body
	);
};

// Min/Max EV Content Component - Integrated keypad without modal overlay
const MinMaxEVContent = ({ filters, activeRowIdx, setFilters, email, onClose }) => {
	const [editingMin, setEditingMin] = useState(false);
	const [editingMax, setEditingMax] = useState(false);
	const [minInput, setMinInput] = useState('');
	const [maxInput, setMaxInput] = useState('');
	const [validationError, setValidationError] = useState('');

	const minEV = filters[activeRowIdx]?.minMaxEv?.[0] ?? null;
	const maxEV = filters[activeRowIdx]?.minMaxEv?.[1] ?? null;

	useEffect(() => {
		if (!editingMin) {
			setMinInput(minEV === null || minEV === undefined ? '' : minEV.toString());
		}
	}, [minEV, editingMin]);

	useEffect(() => {
		if (!editingMax) {
			setMaxInput(maxEV === null || maxEV === undefined ? '' : maxEV.toString());
		}
	}, [maxEV, editingMax]);

	const handleKey = (num, isMin) => {
		if (validationError) {
			setValidationError('');
		}
		const currentInput = isMin ? minInput : maxInput;
		if (currentInput.length >= 5) return;
		if (currentInput === '0' || currentInput === '-0') {
			if (isMin) {
				setMinInput(currentInput.startsWith('-') ? '-' + num : num);
			} else {
				setMaxInput(currentInput.startsWith('-') ? '-' + num : num);
			}
		} else {
			if (isMin) {
				setMinInput(minInput + num);
			} else {
				setMaxInput(maxInput + num);
			}
		}
	};

	const handleToggleSign = (isMin) => {
		if (validationError) {
			setValidationError('');
		}
		if (isMin) {
			setMinInput(minInput.startsWith('-') ? minInput.slice(1) : '-' + minInput);
		} else {
			setMaxInput(maxInput.startsWith('-') ? maxInput.slice(1) : '-' + maxInput);
		}
	};

	const handleBackspace = (isMin) => {
		if (validationError) {
			setValidationError('');
		}
		if (isMin) {
			if (minInput.length === 1) {
				setMinInput('0');
			} else {
				setMinInput(minInput.slice(0, -1));
			}
		} else {
			if (maxInput.length === 1) {
				setMaxInput('0');
			} else {
				setMaxInput(maxInput.slice(0, -1));
			}
		}
	};

	const handleSetToAny = async (isMin) => {
		setValidationError('');
		const copy = JSON.parse(JSON.stringify(filters));
		if (isMin) {
			copy[activeRowIdx].minMaxEv[0] = -9999999;
		} else {
			copy[activeRowIdx].minMaxEv[1] = 9999999;
		}
		setFilters(copy);
		const userdb = JSON.parse(window.localStorage.getItem('userdb'));
		const selectedName = userdb.selected_query || (userdb.queries?.[0]?.[0]?.name ?? 'default');
		const idx = userdb.queries.findIndex(q => q?.[0]?.name === selectedName);
		if (idx !== -1) {
			userdb.queries[idx] = copy;
			window.localStorage.setItem('userdb', JSON.stringify(userdb));
			await saveUserInfoOutliers(copy, email);
		}
		if (isMin) {
			setEditingMin(false);
		} else {
			setEditingMax(false);
		}
	};

	const handleSubmit = async (isMin) => {
		setValidationError('');
		const input = isMin ? minInput : maxInput;
		if (input === '' || input === '-') {
			const copy = JSON.parse(JSON.stringify(filters));
			if (isMin) {
				copy[activeRowIdx].minMaxEv[0] = -9999999;
			} else {
				copy[activeRowIdx].minMaxEv[1] = 9999999;
			}
			setFilters(copy);
			const userdb = JSON.parse(window.localStorage.getItem('userdb'));
			const selectedName = userdb.selected_query || (userdb.queries?.[0]?.[0]?.name ?? 'default');
			const idx = userdb.queries.findIndex(q => q?.[0]?.name === selectedName);
			if (idx !== -1) {
				userdb.queries[idx] = copy;
				window.localStorage.setItem('userdb', JSON.stringify(userdb));
				await saveUserInfoOutliers(copy, email);
			}
			if (isMin) {
				setEditingMin(false);
			} else {
				setEditingMax(false);
			}
			return;
		}
		const val = parseInt(input, 10);
		if (!isNaN(val)) {
			const copy = JSON.parse(JSON.stringify(filters));
			const currentMin = copy[activeRowIdx].minMaxEv[0];
			const currentMax = copy[activeRowIdx].minMaxEv[1];
			
			if (isMin) {
				if (currentMax !== null && currentMax !== undefined && val > currentMax) {
					setValidationError('Minimum EV cannot be greater than maximum EV');
					return;
				}
				copy[activeRowIdx].minMaxEv[0] = val;
			} else {
				if (currentMin !== null && currentMin !== undefined && val < currentMin) {
					setValidationError('Maximum EV cannot be less than minimum EV');
					return;
				}
				copy[activeRowIdx].minMaxEv[1] = val;
			}
			setFilters(copy);
			const userdb = JSON.parse(window.localStorage.getItem('userdb'));
			const selectedName = userdb.selected_query || (userdb.queries?.[0]?.[0]?.name ?? 'default');
			const idx = userdb.queries.findIndex(q => q?.[0]?.name === selectedName);
			if (idx !== -1) {
				userdb.queries[idx] = copy;
				window.localStorage.setItem('userdb', JSON.stringify(userdb));
				await saveUserInfoOutliers(copy, email);
			}
			if (isMin) {
				setEditingMin(false);
			} else {
				setEditingMax(false);
			}
		}
	};

	const formatEV = (value) => {
		if (value === null || value === undefined) return 'Any';
		return `${value}%`;
	};

	const currentInput = editingMin ? minInput : (editingMax ? maxInput : '');
	const isEditing = editingMin || editingMax;

	return createPortal(
		<div style={{zIndex: 10000}} className="bg-gray-900 text-white fixed top-0 left-0 w-full h-full p-4 flex flex-col">
			<div className="flex items-center justify-between mb-6">
				<div className="flex items-center gap-3">
					{onClose && (
						<button
							className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-800/80 border border-gray-600 hover:bg-gray-700 hover:border-gray-500 transition-all duration-200 backdrop-blur-sm"
							aria-label="Back"
							onClick={onClose}
						>
							<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
							</svg>
						</button>
					)}
					<div>
						<h2 className="text-xl font-bold text-white">Min/Max Discrepancy</h2>
					</div>
				</div>
				<button 
					onClick={onClose} 
					className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-800/80 border border-gray-600 hover:bg-gray-700 hover:border-gray-500 transition-all duration-200 backdrop-blur-sm"
					aria-label="Close"
				>
					<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
					</svg>
				</button>
			</div>
			
			<div className="flex-1 overflow-y-auto space-y-6">
				{(!isEditing || editingMin) && (
					<div className="space-y-3">
						<p className="text-base text-gray-400">Minimum Discrepancy</p>
						<div className="flex items-center justify-center py-4">
							<span className="text-5xl font-bold text-white">
								{editingMin ? `${currentInput || '0'}%` : formatEV(minEV)}
							</span>
						</div>
						{!editingMin && !editingMax && (
							<button
								onClick={() => {
									setEditingMin(true);
									setMinInput(minEV === null || minEV === undefined ? '' : minEV.toString());
									setValidationError('');
								}}
								className="w-full bg-gray-800 text-white py-3 rounded-xl hover:bg-gray-700 transition-colors"
							>
								Edit Minimum
							</button>
						)}
					</div>
				)}

				{(!isEditing || editingMax) && (
					<div className="space-y-3">
						<p className="text-base text-gray-400">Maximum Discrepancy</p>
						<div className="flex items-center justify-center py-4">
							<span className="text-5xl font-bold text-white">
								{editingMax ? `${currentInput || '0'}%` : formatEV(maxEV)}
							</span>
						</div>
						{!editingMin && !editingMax && (
							<button
								onClick={() => {
									setEditingMax(true);
									setMaxInput(maxEV === null || maxEV === undefined ? '' : maxEV.toString());
									setValidationError('');
								}}
								className="w-full bg-gray-800 text-white py-3 rounded-xl hover:bg-gray-700 transition-colors"
							>
								Edit Maximum
							</button>
						)}
					</div>
				)}

				{validationError && (
					<div className="text-red-500 text-center text-sm mt-2">
						{validationError}
					</div>
				)}

				{isEditing && (
					<div className="w-full max-w-xs mx-auto">
						<div className="grid grid-cols-3 gap-3 mb-4">
							{[1,2,3,4,5,6,7,8,9].map(n => (
								<button
									key={n}
									className="bg-gray-800 text-white text-2xl rounded-xl py-4 hover:bg-blue-700 active:bg-blue-700 focus:outline-none focus:ring-0 focus:bg-gray-800 transition-colors font-semibold touch-manipulation"
									onClick={() => handleKey(n.toString(), editingMin)}
									onTouchEnd={(e) => {
										e.currentTarget.blur();
									}}
									onMouseUp={(e) => {
										e.currentTarget.blur();
									}}
								>
									{n}
								</button>
							))}
							<button
								className="bg-gray-800 text-white text-2xl rounded-xl py-4 hover:bg-blue-700 active:bg-blue-700 focus:outline-none focus:ring-0 focus:bg-gray-800 transition-colors font-semibold touch-manipulation"
								onClick={() => handleToggleSign(editingMin)}
								onTouchEnd={(e) => {
									e.currentTarget.blur();
								}}
								onMouseUp={(e) => {
									e.currentTarget.blur();
								}}
							>
								±
							</button>
							<button
								className="bg-gray-800 text-white text-2xl rounded-xl py-4 hover:bg-blue-700 active:bg-blue-700 focus:outline-none focus:ring-0 focus:bg-gray-800 transition-colors font-semibold touch-manipulation"
								onClick={() => handleKey('0', editingMin)}
								onTouchEnd={(e) => {
									e.currentTarget.blur();
								}}
								onMouseUp={(e) => {
									e.currentTarget.blur();
								}}
							>
								0
							</button>
							<button
								className="bg-gray-800 text-white text-2xl rounded-xl py-4 hover:bg-blue-700 active:bg-blue-700 focus:outline-none focus:ring-0 focus:bg-gray-800 transition-colors font-semibold touch-manipulation"
								onClick={() => handleBackspace(editingMin)}
								onTouchEnd={(e) => {
									e.currentTarget.blur();
								}}
								onMouseUp={(e) => {
									e.currentTarget.blur();
								}}
							>
								←
							</button>
							<button
								className="bg-blue-600 text-white text-xl rounded-xl py-4 hover:bg-blue-700 active:bg-blue-700 focus:outline-none focus:ring-0 focus:bg-blue-600 transition-colors col-span-3 font-semibold touch-manipulation"
								onClick={() => handleSubmit(editingMin)}
								onTouchEnd={(e) => {
									e.currentTarget.blur();
								}}
								onMouseUp={(e) => {
									e.currentTarget.blur();
								}}
							>
								Save
							</button>
						</div>
						<div className="flex gap-2">
							<button
								onClick={() => handleSetToAny(editingMin)}
								className="flex-1 text-gray-400 hover:text-white py-2 rounded-lg border border-gray-600 hover:border-gray-500"
							>
								Set to Any
							</button>
							<button
								onClick={() => {
									setEditingMin(false);
									setEditingMax(false);
									setValidationError('');
								}}
								className="flex-1 text-gray-400 hover:text-white py-2 rounded-lg"
							>
								Cancel
							</button>
						</div>
					</div>
				)}
			</div>
		</div>,
		document.body
	);
};

// Min/Max Odds Content Component - Integrated keypad without modal overlay
const MinMaxOddsContent = ({ filters, activeRowIdx, setFilters, email, onClose }) => {
	const [editingMin, setEditingMin] = useState(false);
	const [editingMax, setEditingMax] = useState(false);
	const [minInput, setMinInput] = useState('');
	const [maxInput, setMaxInput] = useState('');
	const [validationError, setValidationError] = useState('');

	const minOdds = filters[activeRowIdx]?.minMaxOdds?.[0] ?? null;
	const maxOdds = filters[activeRowIdx]?.minMaxOdds?.[1] ?? null;

	useEffect(() => {
		if (!editingMin) {
			setMinInput(minOdds === null || minOdds === undefined ? '' : minOdds.toString());
		}
	}, [minOdds, editingMin]);

	useEffect(() => {
		if (!editingMax) {
			setMaxInput(maxOdds === null || maxOdds === undefined ? '' : maxOdds.toString());
		}
	}, [maxOdds, editingMax]);

	const handleKey = (num, isMin) => {
		if (validationError) {
			setValidationError('');
		}
		const currentInput = isMin ? minInput : maxInput;
		if (currentInput.length >= 7) return;
		if (currentInput === '0' || currentInput === '-0') {
			if (isMin) {
				setMinInput(currentInput.startsWith('-') ? '-' + num : num);
			} else {
				setMaxInput(currentInput.startsWith('-') ? '-' + num : num);
			}
		} else {
			if (isMin) {
				setMinInput(minInput + num);
			} else {
				setMaxInput(maxInput + num);
			}
		}
	};

	const handleToggleSign = (isMin) => {
		if (validationError) {
			setValidationError('');
		}
		if (isMin) {
			setMinInput(minInput.startsWith('-') ? minInput.slice(1) : '-' + minInput);
		} else {
			setMaxInput(maxInput.startsWith('-') ? maxInput.slice(1) : '-' + maxInput);
		}
	};

	const handleBackspace = (isMin) => {
		if (validationError) {
			setValidationError('');
		}
		if (isMin) {
			if (minInput.length === 1 || (minInput.length === 2 && minInput.startsWith('-'))) {
				setMinInput('0');
			} else {
				setMinInput(minInput.slice(0, -1));
			}
		} else {
			if (maxInput.length === 1 || (maxInput.length === 2 && maxInput.startsWith('-'))) {
				setMaxInput('0');
			} else {
				setMaxInput(maxInput.slice(0, -1));
			}
		}
	};

	const handleSetToAny = async (isMin) => {
		setValidationError('');
		const copy = JSON.parse(JSON.stringify(filters));
		if (isMin) {
			copy[activeRowIdx].minMaxOdds[0] = -9999999;
		} else {
			copy[activeRowIdx].minMaxOdds[1] = 9999999;
		}
		setFilters(copy);
		const userdb = JSON.parse(window.localStorage.getItem('userdb'));
		const selectedName = userdb.selected_query || (userdb.queries?.[0]?.[0]?.name ?? 'default');
		const idx = userdb.queries.findIndex(q => q?.[0]?.name === selectedName);
		if (idx !== -1) {
			userdb.queries[idx] = copy;
			window.localStorage.setItem('userdb', JSON.stringify(userdb));
			await saveUserInfoOutliers(copy, email);
		}
		if (isMin) {
			setEditingMin(false);
		} else {
			setEditingMax(false);
		}
	};

	const handleSubmit = async (isMin) => {
		setValidationError('');
		const input = isMin ? minInput : maxInput;
		if (input === '' || input === '-') {
			const copy = JSON.parse(JSON.stringify(filters));
			if (isMin) {
				copy[activeRowIdx].minMaxOdds[0] = -9999999;
			} else {
				copy[activeRowIdx].minMaxOdds[1] = 9999999;
			}
			setFilters(copy);
			const userdb = JSON.parse(window.localStorage.getItem('userdb'));
			const selectedName = userdb.selected_query || (userdb.queries?.[0]?.[0]?.name ?? 'default');
			const idx = userdb.queries.findIndex(q => q?.[0]?.name === selectedName);
			if (idx !== -1) {
				userdb.queries[idx] = copy;
				window.localStorage.setItem('userdb', JSON.stringify(userdb));
				await saveUserInfoOutliers(copy, email);
			}
			if (isMin) {
				setEditingMin(false);
			} else {
				setEditingMax(false);
			}
			return;
		}
		const val = parseInt(input, 10);
		if (!isNaN(val)) {
			if (val > -100 && val < 100) {
				setValidationError('Odds must be less than or equal to -100 or greater than or equal to +100');
				return;
			}
			
			const copy = JSON.parse(JSON.stringify(filters));
			const currentMin = copy[activeRowIdx].minMaxOdds[0];
			const currentMax = copy[activeRowIdx].minMaxOdds[1];
			
			if (isMin) {
				if (currentMax !== null && currentMax !== undefined && val > currentMax) {
					setValidationError('Minimum odds cannot be greater than maximum odds');
					return;
				}
				copy[activeRowIdx].minMaxOdds[0] = val;
			} else {
				if (currentMin !== null && currentMin !== undefined && val < currentMin) {
					setValidationError('Maximum odds cannot be less than minimum odds');
					return;
				}
				copy[activeRowIdx].minMaxOdds[1] = val;
			}
			setFilters(copy);
			const userdb = JSON.parse(window.localStorage.getItem('userdb'));
			const selectedName = userdb.selected_query || (userdb.queries?.[0]?.[0]?.name ?? 'default');
			const idx = userdb.queries.findIndex(q => q?.[0]?.name === selectedName);
			if (idx !== -1) {
				userdb.queries[idx] = copy;
				window.localStorage.setItem('userdb', JSON.stringify(userdb));
				await saveUserInfoOutliers(copy, email);
			}
			if (isMin) {
				setEditingMin(false);
			} else {
				setEditingMax(false);
			}
		}
	};

	const formatOdds = (value) => {
		if (value === null || value === undefined) return 'Any';
		return value > 0 ? `+${value}` : value.toString();
	};

	const currentInput = editingMin ? minInput : (editingMax ? maxInput : '');
	const isEditing = editingMin || editingMax;
	const displayValue = currentInput || '0';
	const formattedDisplay = displayValue === '0' ? '0' : parseInt(displayValue) > 0 ? `+${displayValue}` : displayValue;

	return createPortal(
		<div style={{zIndex: 10000}} className="bg-gray-900 text-white fixed top-0 left-0 w-full h-full p-4 flex flex-col">
			<div className="flex items-center justify-between mb-6">
				<div className="flex items-center gap-3">
					{onClose && (
						<button
							className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-800/80 border border-gray-600 hover:bg-gray-700 hover:border-gray-500 transition-all duration-200 backdrop-blur-sm"
							aria-label="Back"
							onClick={onClose}
						>
							<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
							</svg>
						</button>
					)}
					<div>
						<h2 className="text-xl font-bold text-white">Min/Max Odds</h2>
					</div>
				</div>
				<button 
					onClick={onClose} 
					className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-800/80 border border-gray-600 hover:bg-gray-700 hover:border-gray-500 transition-all duration-200 backdrop-blur-sm"
					aria-label="Close"
				>
					<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
					</svg>
				</button>
			</div>
			
			<div className="flex-1 overflow-y-auto space-y-6">
				{(!isEditing || editingMin) && (
					<div className="space-y-3">
						<p className="text-base text-gray-400">Minimum Odds</p>
						<div className="flex items-center justify-center py-4">
							<span className="text-5xl font-bold text-white">
								{editingMin ? formattedDisplay : formatOdds(minOdds)}
							</span>
						</div>
						{!editingMin && !editingMax && (
							<button
								onClick={() => {
									setEditingMin(true);
									setMinInput(minOdds === null || minOdds === undefined ? '' : minOdds.toString());
									setValidationError('');
								}}
								className="w-full bg-gray-800 text-white py-3 rounded-xl hover:bg-gray-700 transition-colors"
							>
								Edit Minimum
							</button>
						)}
					</div>
				)}

				{(!isEditing || editingMax) && (
					<div className="space-y-3">
						<p className="text-base text-gray-400">Maximum Odds</p>
						<div className="flex items-center justify-center py-4">
							<span className="text-5xl font-bold text-white">
								{editingMax ? formattedDisplay : formatOdds(maxOdds)}
							</span>
						</div>
						{!editingMin && !editingMax && (
							<button
								onClick={() => {
									setEditingMax(true);
									setMaxInput(maxOdds === null || maxOdds === undefined ? '' : maxOdds.toString());
									setValidationError('');
								}}
								className="w-full bg-gray-800 text-white py-3 rounded-xl hover:bg-gray-700 transition-colors"
							>
								Edit Maximum
							</button>
						)}
					</div>
				)}

				{validationError && (
					<div className="text-red-500 text-center text-sm mt-2">
						{validationError}
					</div>
				)}

				{isEditing && (
					<div className="w-full max-w-xs mx-auto">
						<div className="grid grid-cols-3 gap-3 mb-4">
							{[1,2,3,4,5,6,7,8,9].map(n => (
								<button
									key={n}
									className="bg-gray-800 text-white text-2xl rounded-xl py-4 hover:bg-blue-700 active:bg-blue-700 focus:outline-none focus:ring-0 focus:bg-gray-800 transition-colors font-semibold touch-manipulation"
									onClick={() => handleKey(n.toString(), editingMin)}
									onTouchEnd={(e) => {
										e.currentTarget.blur();
									}}
									onMouseUp={(e) => {
										e.currentTarget.blur();
									}}
								>
									{n}
								</button>
							))}
							<button
								className="bg-gray-800 text-white text-2xl rounded-xl py-4 hover:bg-blue-700 active:bg-blue-700 focus:outline-none focus:ring-0 focus:bg-gray-800 transition-colors font-semibold touch-manipulation"
								onClick={() => handleToggleSign(editingMin)}
								onTouchEnd={(e) => {
									e.currentTarget.blur();
								}}
								onMouseUp={(e) => {
									e.currentTarget.blur();
								}}
							>
								±
							</button>
							<button
								className="bg-gray-800 text-white text-2xl rounded-xl py-4 hover:bg-blue-700 active:bg-blue-700 focus:outline-none focus:ring-0 focus:bg-gray-800 transition-colors font-semibold touch-manipulation"
								onClick={() => handleKey('0', editingMin)}
								onTouchEnd={(e) => {
									e.currentTarget.blur();
								}}
								onMouseUp={(e) => {
									e.currentTarget.blur();
								}}
							>
								0
							</button>
							<button
								className="bg-gray-800 text-white text-2xl rounded-xl py-4 hover:bg-blue-700 active:bg-blue-700 focus:outline-none focus:ring-0 focus:bg-gray-800 transition-colors font-semibold touch-manipulation"
								onClick={() => handleBackspace(editingMin)}
								onTouchEnd={(e) => {
									e.currentTarget.blur();
								}}
								onMouseUp={(e) => {
									e.currentTarget.blur();
								}}
							>
								←
							</button>
							<button
								className="bg-blue-600 text-white text-xl rounded-xl py-4 hover:bg-blue-700 active:bg-blue-700 focus:outline-none focus:ring-0 focus:bg-blue-600 transition-colors col-span-3 font-semibold touch-manipulation"
								onClick={() => handleSubmit(editingMin)}
								onTouchEnd={(e) => {
									e.currentTarget.blur();
								}}
								onMouseUp={(e) => {
									e.currentTarget.blur();
								}}
							>
								Save
							</button>
						</div>
						<div className="flex gap-2">
							<button
								onClick={() => handleSetToAny(editingMin)}
								className="flex-1 text-gray-400 hover:text-white py-2 rounded-lg border border-gray-600 hover:border-gray-500"
							>
								Set to Any
							</button>
							<button
								onClick={() => {
									setEditingMin(false);
									setEditingMax(false);
									setValidationError('');
								}}
								className="flex-1 text-gray-400 hover:text-white py-2 rounded-lg"
							>
								Cancel
							</button>
						</div>
					</div>
				)}
			</div>
		</div>,
		document.body
	);
};

// Market Depth Content Component - Outlier-specific filter
const MarketDepthContent = ({ filters, activeRowIdx, setFilters, email, onClose }) => {
	const [input, setInput] = useState((filters[activeRowIdx]?.marketDepth || 3).toString());
	const [validationError, setValidationError] = useState('');

	useEffect(() => {
		setInput((filters[activeRowIdx]?.marketDepth || 3).toString());
		setValidationError('');
	}, [filters, activeRowIdx]);

	const handleKey = (num) => {
		if (validationError) {
			setValidationError('');
		}
		setInput((currentInput) => {
			// If input is already 2 digits, we can't add more
			if (currentInput.length >= 2) return currentInput;
			
			// If input is "0", replace it with the new number
			if (currentInput === '0') {
				return num;
			} else {
				// Otherwise, append the number (allow building multi-digit numbers)
				return currentInput + num;
			}
		});
	};

	const handleBackspace = () => {
		if (validationError) {
			setValidationError('');
		}
		setInput((currentInput) => {
			if (currentInput.length === 1) {
				return '0';
			} else {
				return currentInput.slice(0, -1);
			}
		});
	};

	const handleSetToAny = async () => {
		setValidationError('');
		setInput('3');
		const copy = JSON.parse(JSON.stringify(filters));
		copy[activeRowIdx].marketDepth = 3;
		setFilters(copy);
		const userdb = JSON.parse(window.localStorage.getItem('userdb'));
		const selectedName = userdb.selected_query || (userdb.queries?.[0]?.[0]?.name ?? 'default');
		const idx = userdb.queries.findIndex(q => q?.[0]?.name === selectedName);
		if (idx !== -1) {
			userdb.queries[idx] = copy;
			window.localStorage.setItem('userdb', JSON.stringify(userdb));
			await saveUserInfoOutliers(copy, email);
		}
	};

	const handleSubmit = async () => {
		setValidationError('');
		const val = parseInt(input, 10);
		
		if (isNaN(val)) {
			setValidationError('Please enter a valid number');
			return;
		}
		
		if (val < 0 || val > 12) {
			setValidationError('Market depth must be between 1 and 12');
			return;
		}
		
		// If user inputs 0, convert it to 1
		const finalVal = val === 0 ? 1 : val;
		const copy = JSON.parse(JSON.stringify(filters));
		copy[activeRowIdx].marketDepth = finalVal;
		setFilters(copy);
		// Update the input display to show the saved value
		setInput(finalVal.toString());
		const userdb = JSON.parse(window.localStorage.getItem('userdb'));
		const selectedName = userdb.selected_query || (userdb.queries?.[0]?.[0]?.name ?? 'default');
		const idx = userdb.queries.findIndex(q => q?.[0]?.name === selectedName);
		if (idx !== -1) {
			userdb.queries[idx] = copy;
			window.localStorage.setItem('userdb', JSON.stringify(userdb));
			await saveUserInfoOutliers(copy, email);
		}
	};

	return createPortal(
		<div style={{zIndex: 10000}} className="bg-gray-900 text-white fixed top-0 left-0 w-full h-full p-4 flex flex-col">
			<div className="flex items-center justify-between mb-6">
				<div className="flex items-center gap-3">
					{onClose && (
						<button
							className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-800/80 border border-gray-600 hover:bg-gray-700 hover:border-gray-500 transition-all duration-200 backdrop-blur-sm"
							aria-label="Back"
							onClick={onClose}
						>
							<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
							</svg>
						</button>
					)}
					<div>
						<h2 className="text-xl font-bold text-white">Market Depth</h2>
					</div>
				</div>
				<button 
					onClick={onClose} 
					className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-800/80 border border-gray-600 hover:bg-gray-700 hover:border-gray-500 transition-all duration-200 backdrop-blur-sm"
					aria-label="Close"
				>
					<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
					</svg>
				</button>
			</div>
			
			<div className="flex-1 overflow-y-auto space-y-4">
				<div className="space-y-4">
					<p className="text-base text-gray-400">
						Minimum number of books in market average.
					</p>
					
					<div className="flex items-center justify-center py-6">
						<span className="text-5xl font-bold text-white">
							{parseInt(input)}
						</span>
					</div>
					
					{validationError && (
						<div className="text-red-500 text-center text-sm mt-2">
							{validationError}
						</div>
					)}
					
					<div className="w-full max-w-xs mx-auto">
						<div className="grid grid-cols-3 gap-3 mb-6">
							{[1,2,3,4,5,6,7,8,9].map(n => (
								<button
									key={n}
									className="bg-gray-800 text-white text-2xl rounded-xl py-5 hover:bg-blue-700 active:bg-blue-700 focus:outline-none focus:ring-0 focus:bg-gray-800 transition-colors font-semibold touch-manipulation"
									onClick={() => handleKey(n.toString())}
									onTouchEnd={(e) => {
										e.currentTarget.blur();
									}}
									onMouseUp={(e) => {
										e.currentTarget.blur();
									}}
								>
									{n}
								</button>
							))}
							<div></div>
							<button
								className="bg-gray-800 text-white text-2xl rounded-xl py-5 hover:bg-blue-700 active:bg-blue-700 focus:outline-none focus:ring-0 focus:bg-gray-800 transition-colors font-semibold touch-manipulation"
								onClick={() => handleKey('0')}
								onTouchEnd={(e) => {
									e.currentTarget.blur();
								}}
								onMouseUp={(e) => {
									e.currentTarget.blur();
								}}
							>
								0
							</button>
							<button
								className="bg-gray-800 text-white text-2xl rounded-xl py-5 hover:bg-blue-700 active:bg-blue-700 focus:outline-none focus:ring-0 focus:bg-gray-800 transition-colors font-semibold touch-manipulation"
								onClick={handleBackspace}
								onTouchEnd={(e) => {
									e.currentTarget.blur();
								}}
								onMouseUp={(e) => {
									e.currentTarget.blur();
								}}
							>
								←
							</button>
						</div>
						<div className="flex gap-2 pt-2">
							<button
								onClick={handleSetToAny}
								className="flex-1 text-gray-400 hover:text-white py-3 rounded-lg border border-gray-600 hover:border-gray-500"
							>
								Set to Default
							</button>
							<button
								className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 active:bg-blue-700 focus:outline-none focus:ring-0 focus:bg-blue-600 transition-colors font-semibold touch-manipulation"
								onClick={handleSubmit}
								onTouchEnd={(e) => {
									e.currentTarget.blur();
								}}
								onMouseUp={(e) => {
									e.currentTarget.blur();
								}}
							>
								Save
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>,
		document.body
	);
};

// FilterSidebar Component - Generic modal for filter selection
function FilterSidebar(props) {
	const { onClose, setFilters, filters, options, property, propertyName, propertyNameSingular, groupBy, logo, children, index } = props;
	const [sidebarFilters, setSidebarFilters] = useState(
		options
			?.map((sb, idx) => ({
				id: idx + 1,
				name: sb.label,
				value: sb.value,
				checked: filters?.[index]?.[property]?.some((s) => {
					// For sportsbooks/sharpBooks, use isSportsbookEquivalent
					if (property === 'sportsbooks' || property === 'sharpBooks') {
						return isSportsbookEquivalent(s.value, sb.value);
					}
					// For other properties (leagues, sports, markets, etc.), use direct value comparison
					return s.value === sb.value || s.label === sb.value || s.value === sb.label;
				}) ? true : false,
				...sb,
			}))
			.map((s) => ({ ...s }))
	);

	const [searchTerm, setSearchTerm] = useState('');
	const [expandedTypes, setExpandedTypes] = useState({});
	const [expandedTimes, setExpandedTimes] = useState({});
	
	// Check if this is markets filter - use special categorization
	const isMarketsFilter = property === 'markets';
	
	// Categorize markets if this is markets filter
	const categorizedMarkets = useMemo(() => {
		if (!isMarketsFilter) return null;
		return categorizeMarkets(options.map(o => o.value || o.label));
	}, [isMarketsFilter, options]);
	
	// Filter categorized markets by search term
	const filteredCategorizedMarkets = useMemo(() => {
		if (!isMarketsFilter || !categorizedMarkets || !searchTerm) return categorizedMarkets;
		
		const filtered = {};
		Object.keys(categorizedMarkets).forEach(type => {
			filtered[type] = {};
			Object.keys(categorizedMarkets[type]).forEach(time => {
				const markets = categorizedMarkets[type][time].filter(m => 
					m.label.toLowerCase().includes(searchTerm.toLowerCase())
				);
				if (markets.length > 0) {
					filtered[type][time] = markets;
				}
			});
			if (Object.keys(filtered[type]).length === 0) {
				delete filtered[type];
			}
		});
		return filtered;
	}, [isMarketsFilter, categorizedMarkets, searchTerm]);
	
	// Sort types and times for markets
	const typeOrder = ['Moneyline', 'Spread', 'Total', 'Team', 'Player Prop', 'Race To', 'Other'];
	const sortedMarketTypes = filteredCategorizedMarkets ? Object.keys(filteredCategorizedMarkets).sort((a, b) => {
		const indexA = typeOrder.indexOf(a);
		const indexB = typeOrder.indexOf(b);
		if (indexA === -1 && indexB === -1) return a.localeCompare(b);
		if (indexA === -1) return 1;
		if (indexB === -1) return -1;
		return indexA - indexB;
	}) : [];
	
	const timeOrder = [
		'Game',
		'1st Half', '2nd Half',
		'1st Quarter', '2nd Quarter', '3rd Quarter', '4th Quarter', '1st 3 Quarter',
		'1st Period', '2nd Period', '3rd Period',
		'1st 10 Mins', '1st 5 Mins', 'Set', 'Inning', 'Map',
		'Golf', 'Single Over', '1st X Overs', '1st 2 innings', '1st 4 innings', '1st 7 innings', '1st 6 innings', '1st Round', '1st 3 Holes', '2nd Round'
	];
	
	const sortMarketTimes = (times) => {
		return times.sort((a, b) => {
			const indexA = timeOrder.indexOf(a);
			const indexB = timeOrder.indexOf(b);
			if (indexA === -1 && indexB === -1) return a.localeCompare(b);
			if (indexA === -1) return 1;
			if (indexB === -1) return -1;
			return indexA - indexB;
		});
	};
	
	const toggleType = (type) => {
		setExpandedTypes(prev => ({
			...prev,
			[type]: prev[type] === true ? false : true
		}));
	};
	
	const toggleTime = (typeTime) => {
		setExpandedTimes(prev => ({
			...prev,
			[typeTime]: prev[typeTime] === true ? false : true
		}));
	};
	
	// Initialize expanded states for markets - save/restore state
	const prevSearchTermRef = useRef('');
	const savedStateRef = useRef({ types: {}, times: {} });
	const currentExpandedTypesRef = useRef({});
	const currentExpandedTimesRef = useRef({});
	
	useEffect(() => {
		if (!isMarketsFilter) return;
		currentExpandedTypesRef.current = expandedTypes;
	}, [isMarketsFilter, expandedTypes]);
	
	useEffect(() => {
		if (!isMarketsFilter) return;
		currentExpandedTimesRef.current = expandedTimes;
	}, [isMarketsFilter, expandedTimes]);
	
	useEffect(() => {
		if (!isMarketsFilter) return;
		
		const wasSearching = prevSearchTermRef.current.length > 0;
		const isSearching = searchTerm.length > 0;
		
		if (isSearching && !wasSearching) {
			savedStateRef.current.types = { ...currentExpandedTypesRef.current };
			savedStateRef.current.times = { ...currentExpandedTimesRef.current };
			
			const allTypes = Object.keys(filteredCategorizedMarkets || {});
			const allTimes = {};
			allTypes.forEach(type => {
				allTimes[type] = Object.keys((filteredCategorizedMarkets || {})[type] || {});
			});
			setExpandedTypes(Object.fromEntries(allTypes.map(t => [t, true])));
			setExpandedTimes(Object.fromEntries(Object.entries(allTimes).flatMap(([type, times]) => 
				times.map(time => [`${type}-${time}`, true])
			)));
		} else if (isSearching && wasSearching) {
			const allTypes = Object.keys(filteredCategorizedMarkets || {});
			const allTimes = {};
			allTypes.forEach(type => {
				allTimes[type] = Object.keys((filteredCategorizedMarkets || {})[type] || {});
			});
			setExpandedTypes(Object.fromEntries(allTypes.map(t => [t, true])));
			setExpandedTimes(Object.fromEntries(Object.entries(allTimes).flatMap(([type, times]) => 
				times.map(time => [`${type}-${time}`, true])
			)));
		} else if (!isSearching && wasSearching) {
			setExpandedTypes(savedStateRef.current.types);
			setExpandedTimes(savedStateRef.current.times);
		}
		
		prevSearchTermRef.current = searchTerm;
	}, [isMarketsFilter, searchTerm, filteredCategorizedMarkets]);

	const toggleCheckbox = (id) => {
		setSidebarFilters((prev) => prev.map((s) => (s.id === id ? { ...s, checked: !s.checked } : s)));
	};

	const clearAll = () => {
		setSidebarFilters((prev) => prev.map((s) => ({ ...s, checked: false })));
	};

	const saveFilters = async () => {
		const selected = sidebarFilters.filter((s) => s.checked);
		const copy = JSON.parse(JSON.stringify(filters));
		copy[index][property] = selected.map((s) => {
			return { label: s.name, value: s.name };
		});
		setFilters(copy);

		await saveUserInfoOutliers(copy, JSON.parse(window.localStorage.getItem('userdb')).email);
	};

	// Custom mapping for corrections and popular category
	const customLeagueSportMap = {
		'NBA': 'popular',
		'NHL': 'popular',
		'NFL': 'popular',
		'MLB': 'popular',
		'NCAAB': 'popular',
		'NCAAF': 'popular',
		'ATP': 'tennis',
		'ATP Challenger': 'tennis',
		'WTA': 'tennis',
		'Belarus - Premier League': 'soccer',
		'Spain - La Liga': 'soccer',
		'Spain - La Liga 2': 'soccer',
		'CFL': 'football',
		'CS2': 'esports',
		'Dota 2': 'esports',
		'League of Legends': 'esports',
		'Rocket League': 'esports',
		'Valorant': 'esports',
		'NCAAH': 'hockey',
		'PGA': 'golf',
		'UFC': 'mma',
		'International - Tours': 'tennis',
		'International - Tours Women': 'tennis',
	};
	
	let groupedFilters = {};
	let useGrouping = !!groupBy;

	if (useGrouping) {
		sidebarFilters
			.filter((s) => s.name.toLowerCase().includes(searchTerm.toLowerCase()))
			.forEach((item) => {
				const leagueValue = item.value || item.name;
				const sport = customLeagueSportMap[leagueValue] || 
				             LEAGUE_SPORT_MAP[leagueValue] || 
				             item[groupBy] || 
				             'other';
				
				if (!groupedFilters[sport]) {
					groupedFilters[sport] = [];
				}
				groupedFilters[sport].push(item);
			});
		
		const sortedGroupKeys = Object.keys(groupedFilters).sort((a, b) => {
			if (a === 'popular') return -1;
			if (b === 'popular') return 1;
			if (a === 'other') return 1;
			if (b === 'other') return -1;
			return a.localeCompare(b);
		});
		
		const sortedGroupedFilters = {};
		sortedGroupKeys.forEach(key => {
			sortedGroupedFilters[key] = groupedFilters[key];
		});
		groupedFilters = sortedGroupedFilters;
	}

	return createPortal(
		<div style={{zIndex: 10000}} className="bg-gray-900 text-white fixed top-0 left-0 w-full h-full p-4 flex flex-col">
			<div className="flex items-center justify-between mb-6">
				<div className="flex items-center gap-3">
					{onClose && (
						<button
							className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-800/80 border border-gray-600 hover:bg-gray-700 hover:border-gray-500 transition-all duration-200 backdrop-blur-sm"
							aria-label="Back"
							onClick={onClose}
						>
							<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
							</svg>
						</button>
					)}
					<div>
						<h2 className="text-xl font-bold text-white">Select {propertyName}</h2>
					</div>
				</div>
				<button 
					onClick={onClose} 
					className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-800/80 border border-gray-600 hover:bg-gray-700 hover:border-gray-500 transition-all duration-200 backdrop-blur-sm"
					aria-label="Close"
				>
					<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
					</svg>
				</button>
			</div>
			{!children && (
				<>
					<div className="relative mb-6">
						{isMarketsFilter ? (
							<>
								<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
									<img src={magnifying_glass} alt="search" className="w-4 h-4 opacity-70" />
								</div>
								<input
									type="text"
									placeholder="Search for a market"
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
									className="w-full pl-10 pr-4 py-3 rounded-xl bg-gray-800/80 border-2 border-gray-600 placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 backdrop-blur-sm"
									style={{ boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)' }}
								/>
							</>
						) : (
							<>
								<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
									<svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
									</svg>
								</div>
								<input
									type="text"
									placeholder={`Search for a ${propertyNameSingular}`}
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
									className="w-full pl-10 pr-4 py-3 rounded-xl bg-gray-800/80 border-2 border-gray-600 placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 backdrop-blur-sm"
									style={{ boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)' }}
								/>
							</>
						)}
						{searchTerm && (
							<button
								onClick={() => setSearchTerm('')}
								className="absolute inset-y-0 right-0 pr-3 flex items-center"
							>
								<svg className="h-5 w-5 text-gray-400 hover:text-gray-300 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						)}
					</div>
					<div className="flex-1 overflow-y-auto" style={{ color: 'white' }}>
						{isMarketsFilter && filteredCategorizedMarkets ? (
							<div className="space-y-2">
								{sortedMarketTypes.map((type) => {
									const isTypeExpanded = expandedTypes[type] === true;
									const times = sortMarketTimes(Object.keys(filteredCategorizedMarkets[type] || {}));
									
									return (
										<div key={type} className="border border-gray-700 rounded-lg overflow-hidden">
											<div
												onClick={() => toggleType(type)}
												className="flex items-center justify-between p-3 cursor-pointer bg-gray-800/60 hover:bg-gray-700/60 transition-colors"
											>
												<div className="flex items-center gap-2">
													<svg 
														className={`w-4 h-4 text-gray-400 transition-transform ${isTypeExpanded ? 'rotate-90' : ''}`}
														fill="currentColor" 
														viewBox="0 0 20 20"
													>
														<path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
													</svg>
													<span className="text-sm font-semibold text-gray-200">{type}</span>
													<span className="text-xs text-gray-500">
														({Object.values(filteredCategorizedMarkets[type] || {}).flat().length})
													</span>
												</div>
											</div>
											
											{isTypeExpanded && (
												<div className="bg-gray-900">
													{times.map((time) => {
														const typeTimeKey = `${type}-${time}`;
														const isTimeExpanded = expandedTimes[typeTimeKey] === true;
														const markets = filteredCategorizedMarkets[type][time] || [];
														
														return (
															<div key={typeTimeKey} className="border-t border-gray-700">
																<div
																	onClick={() => toggleTime(typeTimeKey)}
																	className="flex items-center justify-between px-4 py-2 cursor-pointer bg-gray-900 hover:bg-gray-800 transition-colors"
																>
																	<div className="flex items-center gap-2">
																		<svg 
																			className={`w-3 h-3 text-gray-500 transition-transform ${isTimeExpanded ? 'rotate-90' : ''}`}
																			fill="currentColor" 
																			viewBox="0 0 20 20"
																		>
																			<path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
																		</svg>
																		<span className="text-xs font-medium text-gray-300">{time}</span>
																		<span className="text-xs text-gray-600">({markets.length})</span>
																	</div>
																</div>
																
																{isTimeExpanded && (
																	<div className="pl-8 pr-2 pb-2">
																		{markets.map((market) => {
																			const marketItem = sidebarFilters.find(s => (s.value || s.name) === market.value);
																			const isChecked = marketItem?.checked || false;
																			
																			return (
																				<div
																					key={market.value}
																					onClick={() => marketItem && toggleCheckbox(marketItem.id)}
																					className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-gray-800 transition-colors"
																				>
																					<img 
																						src={isChecked ? checked_checkbox : unchecked_checkbox} 
																						alt={isChecked ? 'checked' : 'unchecked'}
																						className="w-4 h-4 flex-shrink-0"
																					/>
																					<span className={`text-xs ${isChecked ? 'text-gray-100 font-medium' : 'text-gray-400'}`}>
																						{market.label}
																					</span>
																				</div>
																			);
																		})}
																	</div>
																)}
															</div>
														);
													})}
												</div>
											)}
										</div>
									);
								})}
							</div>
						) : useGrouping ? (
							Object.entries(groupedFilters).map(([group, items]) => (
								<React.Fragment key={group}>
									<div className="text-gray-400 uppercase text-xs tracking-wider mb-3 mt-6 font-semibold">
										{group === 'popular' ? 'Popular' : 
										 group === 'other' ? 'Other' :
										 (() => {
											const sportName = SPORTS.find(s => s.value === group);
											return sportName ? sportName.label.charAt(0).toUpperCase() + sportName.label.slice(1) : group.charAt(0).toUpperCase() + group.slice(1);
										 })()}
									</div>
									<div className="grid grid-cols-3 gap-3 mb-6">
										{items.map((book) => {
											let name = getSportsbookDisplayName(book.name);
											return (
												<button
													key={book.id}
													onClick={() => toggleCheckbox(book.id)}
													className={`relative w-full px-3 py-4 rounded-xl border transition-all duration-200 flex flex-col items-center justify-center group ${
														book.checked 
															? 'bg-blue-600/90 border-blue-500 text-white shadow-lg shadow-blue-500/30' 
															: 'bg-gray-800/50 border-gray-700 text-gray-200 hover:bg-gray-700/80 hover:border-gray-600'
													}`}
													style={{ 
														minHeight: 80,
													}}
												>
													{book.checked && (
														<div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center shadow-md">
															<svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
																<path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
															</svg>
														</div>
													)}
													
													{logo && (
														<div className="mb-2.5">
															<div style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
																<LogoGenerator sportsbook={book.value} />
															</div>
														</div>
													)}
													
													<span className={`text-xs font-medium text-center leading-tight ${book.checked ? 'text-white' : 'text-gray-300'}`} style={{ wordBreak: 'break-word' }}>
														{name}
													</span>
												</button>
											);
										})}
									</div>
								</React.Fragment>
							))
						) : (
							<div className="grid grid-cols-3 gap-3 mb-6">
								{sidebarFilters
									.filter((book) => book.name.toLowerCase().includes(searchTerm.toLowerCase()))
									.map((book) => {
										let name = property === 'sports' 
											? (book.name.toLowerCase() === 'mma' ? 'MMA' : book.name.charAt(0).toUpperCase() + book.name.slice(1).toLowerCase())
											: getSportsbookDisplayName(book.name);
										return (
											<button
												key={book.id}
												onClick={() => toggleCheckbox(book.id)}
												className={`relative w-full px-3 py-4 rounded-xl border transition-all duration-200 flex flex-col items-center justify-center group ${
													book.checked 
														? 'bg-blue-600/90 border-blue-500 text-white shadow-lg shadow-blue-500/30' 
														: 'bg-gray-800/50 border-gray-700 text-gray-200 hover:bg-gray-700/80 hover:border-gray-600'
												}`}
												style={{ 
													minHeight: 80,
												}}
											>
												{book.checked && (
													<div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center shadow-md">
														<svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
															<path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
														</svg>
													</div>
												)}
												
												{logo && (
													<div className="mb-2.5">
														<div style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
															<LogoGenerator sportsbook={book.value} />
														</div>
													</div>
												)}
												
												<span className={`text-xs font-medium text-center leading-tight ${book.checked ? 'text-white' : 'text-gray-300'}`} style={{ wordBreak: 'break-word' }}>
													{name}
												</span>
											</button>
										);
									})}
							</div>
						)}
					</div>
					<div style={{ marginTop: 'auto', paddingTop: 16 }} className="flex flex-col gap-4">
						<div className="flex justify-between items-center px-4 py-3 rounded-xl bg-gray-800/40 border border-gray-700/50">
							<span className="text-sm text-gray-300 font-medium">
								{sidebarFilters.filter(s => s.checked).length} of {sidebarFilters.length} selected
							</span>
							<div className="flex gap-2">
								<button 
									onClick={clearAll} 
									className="text-xs text-gray-300 px-3 py-1.5 rounded-lg border border-gray-700 bg-gray-800/50 hover:bg-gray-700 hover:border-gray-600 transition-all duration-200 font-medium"
								>
									Clear All
								</button>
								<button 
									onClick={() => {
										setSidebarFilters((prev) => prev.map((s) => ({ ...s, checked: true })));
									}} 
									className="text-xs text-gray-300 px-3 py-1.5 rounded-lg border border-gray-700 bg-gray-800/50 hover:bg-gray-700 hover:border-gray-600 transition-all duration-200 font-medium"
								>
									Select All
								</button>
							</div>
						</div>
						
						<button 
							onClick={saveFilters} 
							className="w-full py-4 px-6 rounded-xl bg-blue-600 text-white font-semibold text-base shadow-lg shadow-blue-500/25 hover:bg-blue-700 hover:shadow-xl hover:shadow-blue-500/30 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900"
						>
							<div className="flex items-center justify-center gap-2">
								<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
								</svg>
								Save Filters
							</div>
						</button>
					</div>
				</>
			)}
			{children && <>{children}</>}
		</div>,
		document.body
	);
}

const MobileOutliersFiltersBar = ({ onClose, setFilterView, setActiveRowIdx, filterss, setFiltersBarOpen, setFilters, queries, setUser, user, setQueries, setNewQueryName, newQueryNameRef, email, currentFilterView, currentActiveRowIdx }) => {
	const [isSpecificView, setIsSpecificView] = useState(true);
	const [isAddConditionView, setIsAddConditionView] = useState(false);
	const [isRemoveConditionView, setIsRemoveConditionView] = useState(false);
	const [addConditionRowIdx, setAddConditionRowIdx] = useState(null);
	const [previousView, setPreviousView] = useState(null); // Track if we came from add condition view
	const [editingNameIdx, setEditingNameIdx] = useState(null);
	const [confirmRemoveModal, setConfirmRemoveModal] = useState({ open: false, rowIdx: null, key: null });
	const [saved, setSaved] = useState(false);

	// Helper function to check if a property has been changed from its default value
	const isPropertyChanged = (key, value) => {
		// dateDifference and isOver are internal properties and should never be displayed as filter options
		if (key === 'dateDifference' || key === 'isOver') {
			return false;
		}
		
		if (Array.isArray(value)) {
			if (key === 'minMaxEv') {
				// Check if both are sentinel values, null, or old defaults (0/1000)
				return !((value[0] === -9999999 || value[0] === null || value[0] === 0) && (value[1] === 9999999 || value[1] === null || value[1] === 1000));
			}
			if (key === 'minMaxOdds') {
				// Check if both are sentinel values or null (default/Any)
				return !((value[0] === -9999999 || value[0] === null) && (value[1] === 9999999 || value[1] === null));
			}
			return value.length > 0;
		}
		if (key === 'limit') {
			return value !== null && value !== '';
		}
		if (key === 'hide_alternates') {
			return value === true;
		}
		if (key === 'marketDepth') {
			return true; // Always show market depth filter, even at default value
		}
		if (key === 'date') {
			return value !== null;
		}
		return !!value;
	};

	// Handle escape key
	useEffect(() => {
		const handleEscape = (e) => {
			if (e.key === 'Escape') {
				if (isRemoveConditionView) {
					setIsRemoveConditionView(false);
					setIsSpecificView(true);
					setAddConditionRowIdx(null);
				} else if (isAddConditionView) {
					setIsAddConditionView(false);
					setIsSpecificView(true);
					setAddConditionRowIdx(null);
				}
			}
		};

		window.addEventListener('keydown', handleEscape);
		return () => window.removeEventListener('keydown', handleEscape);
	}, [isRemoveConditionView, isAddConditionView]);

	const handleRemoveCondition = (rowIdx, key) => {
		const copy = JSON.parse(JSON.stringify(filterss));
		if (['sportsbooks', 'sharpBooks', 'sports', 'leagues', 'markets', 'marketTypes'].includes(key)) {
			copy[rowIdx][key] = [];
		} else if (key === 'minMaxEv') {
			copy[rowIdx][key] = [-9999999, 9999999];
		} else if (key === 'minMaxOdds') {
			copy[rowIdx][key] = [-9999999, 9999999];
		} else if (key === 'limit') {
			copy[rowIdx][key] = null;
		} else if (key === 'hide_alternates') {
			copy[rowIdx][key] = false;
		} else if (key === 'marketDepth') {
			copy[rowIdx][key] = 3;
		} else if (key === 'date') {
			copy[rowIdx][key] = null;
		}
		setFilters(copy);
		setConfirmRemoveModal({ open: false, rowIdx: null, key: null });
	};

	return createPortal(
		<div style={{zIndex: 10000}} className="bg-gray-900 text-white fixed top-0 left-0 w-full h-full p-4 flex flex-col">
			{/* Top bar with Back button (if in Add/Remove Condition view), Title, and Close button */}
			<div className="flex items-center justify-between mb-6">
				<div className="flex items-center gap-3">
					{(isAddConditionView || isRemoveConditionView) ? (
						<button
							className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-800/80 border border-gray-600 hover:bg-gray-700 hover:border-gray-500 transition-all duration-200 backdrop-blur-sm"
							aria-label="Back"
							onClick={() => {
								setIsAddConditionView(false);
								setIsRemoveConditionView(false);
								setIsSpecificView(true);
								setAddConditionRowIdx(null);
							}}
						>
							<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
							</svg>
						</button>
					) : null}
					<div>
						<h2 className="text-xl font-bold text-white">{isRemoveConditionView ? 'Remove Conditions' : 'Outlier Filters'}</h2>
					</div>
				</div>
				<button 
					onClick={() => {
						if (!isAddConditionView) {
							onClose();
						} else {
							setIsAddConditionView(false)
							setIsSpecificView(true)
							setAddConditionRowIdx(null)
						}
					}} 
					className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-800/80 border border-gray-600 hover:bg-gray-700 hover:border-gray-500 transition-all duration-200 backdrop-blur-sm"
					aria-label="Close"
				>
					<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
					</svg>
				</button>
			</div>

			{/* Main content area */}
			<div className="flex-1 overflow-y-auto">
				{/* Filter editing view - show when currentFilterView is provided */}
				{currentFilterView && currentActiveRowIdx !== null && (
					<>
						{currentFilterView === 'sportsbooks' && (
							<FilterSidebar
								onClose={() => {
									if (previousView === 'addCondition') {
										setFilterView(null);
										setActiveRowIdx(null);
										setIsAddConditionView(true);
										setPreviousView(null);
									} else {
										setFilterView(null);
										setActiveRowIdx(null);
										setIsAddConditionView(false);
										setIsRemoveConditionView(false);
										setIsSpecificView(true);
									}
								}}
								setFilters={setFilters}
								filters={filterss}
								options={SPORTSBOOKS}
								property="sportsbooks"
								propertyName="Sportsbooks"
								propertyNameSingular="sportsbook"
								groupBy={null}
								logo={true}
								index={currentActiveRowIdx}
							/>
						)}
						
						{currentFilterView === 'sharpBooks' && (
							<FilterSidebar
								onClose={() => {
									if (previousView === 'addCondition') {
										setFilterView(null);
										setActiveRowIdx(null);
										setIsAddConditionView(true);
										setPreviousView(null);
									} else {
										setFilterView(null);
										setActiveRowIdx(null);
										setIsAddConditionView(false);
										setIsRemoveConditionView(false);
										setIsSpecificView(true);
									}
								}}
								setFilters={setFilters}
								filters={filterss}
								options={SPORTSBOOKS}
								property="sharpBooks"
								propertyName="Sharp Books"
								propertyNameSingular="sharp book"
								groupBy={null}
								logo={true}
								index={currentActiveRowIdx}
							/>
						)}
						
						{currentFilterView === 'sports' && (
							<FilterSidebar
								onClose={() => {
									if (previousView === 'addCondition') {
										setFilterView(null);
										setActiveRowIdx(null);
										setIsAddConditionView(true);
										setPreviousView(null);
									} else {
										setFilterView(null);
										setActiveRowIdx(null);
										setIsAddConditionView(false);
										setIsRemoveConditionView(false);
										setIsSpecificView(true);
									}
								}}
								setFilters={setFilters}
								filters={filterss}
								options={SPORTS}
								property="sports"
								propertyName="Sports"
								propertyNameSingular="sport"
								groupBy={null}
								logo={false}
								index={currentActiveRowIdx}
							/>
						)}
						
						{currentFilterView === 'leagues' && (
							<FilterSidebar
								onClose={() => {
									if (previousView === 'addCondition') {
										setFilterView(null);
										setActiveRowIdx(null);
										setIsAddConditionView(true);
										setPreviousView(null);
									} else {
										setFilterView(null);
										setActiveRowIdx(null);
										setIsAddConditionView(false);
										setIsRemoveConditionView(false);
										setIsSpecificView(true);
									}
								}}
								setFilters={setFilters}
								filters={filterss}
								options={LEAGUES}
								property="leagues"
								propertyName="Leagues"
								propertyNameSingular="league"
								groupBy="sport"
								logo={false}
								index={currentActiveRowIdx}
							/>
						)}
						
						{currentFilterView === 'markets' && (
							<FilterSidebar
								onClose={() => {
									if (previousView === 'addCondition') {
										setFilterView(null);
										setActiveRowIdx(null);
										setIsAddConditionView(true);
										setPreviousView(null);
									} else {
										setFilterView(null);
										setActiveRowIdx(null);
										setIsAddConditionView(false);
										setIsRemoveConditionView(false);
										setIsSpecificView(true);
									}
								}}
								setFilters={setFilters}
								filters={filterss}
								options={MARKETS.map((m) => ({ label: m, value: m }))}
								property="markets"
								propertyName="Markets"
								propertyNameSingular="market"
								groupBy={null}
								logo={false}
								index={currentActiveRowIdx}
							/>
						)}
						
						{currentFilterView === 'marketTypes' && (
							<FilterSidebar
								onClose={() => {
									if (previousView === 'addCondition') {
										setFilterView(null);
										setActiveRowIdx(null);
										setIsAddConditionView(true);
										setPreviousView(null);
									} else {
										setFilterView(null);
										setActiveRowIdx(null);
										setIsAddConditionView(false);
										setIsRemoveConditionView(false);
										setIsSpecificView(true);
									}
								}}
								setFilters={setFilters}
								filters={filterss}
								options={[
									{ value: 'Main Markets', label: 'Main Markets' },
									{ value: 'Alternate Markets', label: 'Alternate Markets' },
									{ value: 'Player Props', label: 'Player Props' },
								]}
								property="marketTypes"
								propertyName="Market Types"
								propertyNameSingular="market type"
								groupBy={null}
								logo={false}
								index={currentActiveRowIdx}
							/>
						)}
						
						{currentFilterView === 'minMaxEv' && (
							<MinMaxEVContent
								filters={filterss}
								activeRowIdx={currentActiveRowIdx}
								setFilters={setFilters}
								email={email}
								onClose={() => {
									if (previousView === 'addCondition') {
										setFilterView(null);
										setActiveRowIdx(null);
										setIsAddConditionView(true);
										setPreviousView(null);
									} else {
										setFilterView(null);
										setActiveRowIdx(null);
										setIsAddConditionView(false);
										setIsRemoveConditionView(false);
										setIsSpecificView(true);
									}
								}}
							/>
						)}
						
						{currentFilterView === 'minMaxOdds' && (
							<MinMaxOddsContent
								filters={filterss}
								activeRowIdx={currentActiveRowIdx}
								setFilters={setFilters}
								email={email}
								onClose={() => {
									if (previousView === 'addCondition') {
										setFilterView(null);
										setActiveRowIdx(null);
										setIsAddConditionView(true);
										setPreviousView(null);
									} else {
										setFilterView(null);
										setActiveRowIdx(null);
										setIsAddConditionView(false);
										setIsRemoveConditionView(false);
										setIsSpecificView(true);
									}
								}}
							/>
						)}
						
						{currentFilterView === 'limit' && (
							<LimitFilterContent
								filters={filterss}
								activeRowIdx={currentActiveRowIdx}
								setFilters={setFilters}
								email={email}
								onClose={() => {
									if (previousView === 'addCondition') {
										setFilterView(null);
										setActiveRowIdx(null);
										setIsAddConditionView(true);
										setPreviousView(null);
									} else {
										setFilterView(null);
										setActiveRowIdx(null);
										setIsAddConditionView(false);
										setIsRemoveConditionView(false);
										setIsSpecificView(true);
									}
								}}
							/>
						)}
						
						{currentFilterView === 'hide_alternates' && (
							<HideAlternatesContent
								filters={filterss}
								activeRowIdx={currentActiveRowIdx}
								setFilters={setFilters}
								email={email}
								onClose={() => {
									if (previousView === 'addCondition') {
										setFilterView(null);
										setActiveRowIdx(null);
										setIsAddConditionView(true);
										setPreviousView(null);
									} else {
										setFilterView(null);
										setActiveRowIdx(null);
										setIsAddConditionView(false);
										setIsRemoveConditionView(false);
										setIsSpecificView(true);
									}
								}}
							/>
						)}
						
						{currentFilterView === 'marketDepth' && (
							<MarketDepthContent
								filters={filterss}
								activeRowIdx={currentActiveRowIdx}
								setFilters={setFilters}
								email={email}
								onClose={() => {
									if (previousView === 'addCondition') {
										setFilterView(null);
										setActiveRowIdx(null);
										setIsAddConditionView(true);
										setPreviousView(null);
									} else {
										setFilterView(null);
										setActiveRowIdx(null);
										setIsAddConditionView(false);
										setIsRemoveConditionView(false);
										setIsSpecificView(true);
									}
								}}
							/>
						)}
						
						{currentFilterView === 'date' && (
							createPortal(
								<div style={{zIndex: 10000}} className="bg-gray-900 text-white fixed top-0 left-0 w-full h-full p-4 flex flex-col">
									<div className="flex items-center justify-between mb-6">
										<div className="flex items-center gap-3">
											<button
												className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-800/80 border border-gray-600 hover:bg-gray-700 hover:border-gray-500 transition-all duration-200 backdrop-blur-sm"
												aria-label="Back"
												onClick={() => {
													if (previousView === 'addCondition') {
														setFilterView(null);
														setActiveRowIdx(null);
														setIsAddConditionView(true);
														setPreviousView(null);
													} else {
														setFilterView(null);
														setActiveRowIdx(null);
														setIsAddConditionView(false);
														setIsRemoveConditionView(false);
														setIsSpecificView(true);
													}
												}}
											>
												<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
												</svg>
											</button>
											<div>
												<h2 className="text-xl font-bold text-white">Date Range</h2>
											</div>
										</div>
										<button 
											onClick={() => {
												if (previousView === 'addCondition') {
													setFilterView(null);
													setActiveRowIdx(null);
													setIsAddConditionView(true);
													setPreviousView(null);
												} else {
													setFilterView(null);
													setActiveRowIdx(null);
													setIsAddConditionView(false);
													setIsRemoveConditionView(false);
													setIsSpecificView(true);
												}
											}} 
											className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-800/80 border border-gray-600 hover:bg-gray-700 hover:border-gray-500 transition-all duration-200 backdrop-blur-sm"
											aria-label="Close"
										>
											<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
											</svg>
										</button>
									</div>
									
									<div className="flex-1 overflow-y-auto">
										<DatePickerComponent
											filters={filterss}
											setFilters={setFilters}
											index={currentActiveRowIdx}
											saveUserInfo={saveUserInfoOutliers}
											email={email}
										/>
									</div>
								</div>,
								document.body
							)
						)}
					</>
				)}

				{isSpecificView && !currentFilterView && (
					<div className="flex-1 overflow-y-auto">
						{filterss.map((row, rowIdx) => (
							<div key={rowIdx}>
								{Object.entries(row)
									.filter(([key, value]) => {
										if (key === 'name' || key === 'dateDifference' || key === 'isOver') return false;
										return isPropertyChanged(key, value);
									})
									.map((f) => {
										let displayValue = null;
										let count = null;
										
										if (["sportsbooks", "sharpBooks", "sports", "leagues", "markets", "marketTypes"].includes(f[0])) {
											count = f[1]?.length;
										}
										
										// Handle minMaxEv - show as single row
										if (f[0] === "minMaxEv" && Array.isArray(f[1])) {
											const min = (f[1][0] === null || f[1][0] === undefined || f[1][0] === -9999999 || f[1][0] === 0) ? "Any" : `${f[1][0]}%`;
											const max = (f[1][1] === null || f[1][1] === undefined || f[1][1] === 9999999 || f[1][1] === 1000) ? "Any" : `${f[1][1]}%`;
											
											return (
												<div
													key={f[0]}
													onClick={() => {
														setFilterView("minMaxEv");
														setActiveRowIdx(rowIdx);
													}}
													className="flex justify-between items-center py-4 cursor-pointer hover:bg-[#2a2a2a]"
												>
													<div>
														<p>{propertyToTitleMapping[f[0]] || f[0]}</p>
													</div>
													<div className="flex items-center gap-2">
														<span className="bg-blue-600 text-xs px-2 py-1 rounded-full">{min} / {max}</span>
														<span className="text-gray-400">&gt;</span>
													</div>
												</div>
											);
										}
										
										// Handle minMaxOdds - show as single row
										if (f[0] === "minMaxOdds" && Array.isArray(f[1])) {
											const formatOdds = (value) => {
												if (value === null || value === undefined || value === -9999999 || value === 9999999) return "Any";
												return value > 0 ? `+${value}` : value.toString();
											};
											const min = formatOdds(f[1][0]);
											const max = formatOdds(f[1][1]);
											
											return (
												<div
													key={f[0]}
													onClick={() => {
														setFilterView("minMaxOdds");
														setActiveRowIdx(rowIdx);
													}}
													className="flex justify-between items-center py-4 cursor-pointer hover:bg-[#2a2a2a]"
												>
													<div>
														<p>{propertyToTitleMapping[f[0]] || f[0]}</p>
													</div>
													<div className="flex items-center gap-2">
														<span className="bg-blue-600 text-xs px-2 py-1 rounded-full">{min} / {max}</span>
														<span className="text-gray-400">&gt;</span>
													</div>
												</div>
											);
										}
										
										if (f[0] === "date" && f[1]) {
											try {
												const dateObj = new Date(f[1]);
												displayValue = isNaN(dateObj.getTime()) ? '' : dateObj.toLocaleDateString('en-US');
											} catch {
												displayValue = '';
											}
										}
										
										// Handle hide_alternates
										if (f[0] === 'hide_alternates') {
											return (
												<div
													key={f[0]}
													onClick={() => {
														setFilterView("hide_alternates");
														setActiveRowIdx(rowIdx);
													}}
													style={{ borderBottom: 'none' }}
													className="flex justify-between items-center py-4 cursor-pointer hover:bg-[#2a2a2a]"
												>
													<div>
														<p>{propertyToTitleMapping[f[0]] || f[0]}</p>
													</div>
													<div className="flex items-center gap-2">
														<span className={`bg-blue-600 text-xs px-2 py-1 rounded-full`}>{f[1] ? 'On' : 'Off'}</span>
														<span className="text-gray-400">&gt;</span>
													</div>
												</div>
											);
										}
										
										// Handle limit
										if (f[0] === 'limit') {
											return (
												<div
													key={f[0]}
													onClick={() => {
														setFilterView("limit");
														setActiveRowIdx(rowIdx);
													}}
													style={{ borderBottom: 'none' }}
													className="flex justify-between items-center py-4 cursor-pointer hover:bg-[#2a2a2a]"
												>
													<div>
														<p>{propertyToTitleMapping[f[0]] || f[0]}</p>
													</div>
													<div className="flex items-center gap-2">
														{f[1] ? (
															<span className="bg-blue-600 text-xs px-2 py-1 rounded-full">${Number(f[1]).toLocaleString()}</span>
														) : null}
														<span className="text-gray-400">&gt;</span>
													</div>
												</div>
											);
										}
										
										// Handle marketDepth
										if (f[0] === 'marketDepth') {
											return (
												<div
													key={f[0]}
													onClick={() => {
														setFilterView("marketDepth");
														setActiveRowIdx(rowIdx);
													}}
													style={{ borderBottom: 'none' }}
													className="flex justify-between items-center py-4 cursor-pointer hover:bg-[#2a2a2a]"
												>
													<div>
														<p>{propertyToTitleMapping[f[0]] || f[0]}</p>
													</div>
													<div className="flex items-center gap-2">
														{f[1] ? (
															<span className="bg-blue-600 text-xs px-2 py-1 rounded-full">{f[1]} book{f[1] !== 1 ? 's' : ''}</span>
														) : null}
														<span className="text-gray-400">&gt;</span>
													</div>
												</div>
											);
										}
										
										// Handle all other fields (sportsbooks, sharpBooks, sports, leagues, markets, marketTypes, date)
										return (
											<div
												key={f[0]}
												onClick={() => {
													setFilterView(f[0]);
													setActiveRowIdx(rowIdx);
												}}
												className="flex justify-between items-center py-4 cursor-pointer hover:bg-[#2a2a2a]"
											>
												<div>
													<p>{propertyToTitleMapping[f[0]] || f[0]}</p>
												</div>
												<div className="flex items-center gap-2">
													{["date"].includes(f[0]) && displayValue ? (
														<span className="bg-blue-600 text-xs px-2 py-1 rounded-full">{displayValue}</span>
													) : count !== undefined ? (
														<span className="bg-blue-600 text-xs px-2 py-1 rounded-full">{count}</span>
													) : null}
													<span className="text-gray-400">&gt;</span>
												</div>
											</div>
										);
									})}
								{/* Add Condition and Remove Condition buttons for this query row */}
								<div style={{ width: 'fit-content', marginLeft: 'auto' }} className="mt-6 flex flex-col gap-2">
									<div className="flex gap-2">
										<button
											onClick={() => {
												setIsAddConditionView(true);
												setIsSpecificView(false);
												setIsRemoveConditionView(false);
												setAddConditionRowIdx(rowIdx);
											}}
											className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded"
										>
											Add Condition
										</button>
										<button
											onClick={() => {
												setIsRemoveConditionView(true);
												setIsSpecificView(false);
												setIsAddConditionView(false);
												setAddConditionRowIdx(rowIdx);
											}}
											className="bg-red-600 hover:bg-red-700 text-white text-sm px-4 py-2 rounded"
										>
											Remove Condition
										</button>
									</div>
								</div>
								{/* Divider between query rows, except after the last one */}
								{rowIdx !== (filterss ? filterss.length - 1 : 0) && (
									<div style={{ height: 1, border: '1px solid white', marginTop: 30, opacity: 0.4 }} />
								)}
							</div>
						))}
					</div>
				)}

				{isAddConditionView && addConditionRowIdx !== null && !currentFilterView && (
					<div
						className="flex-1 overflow-y-auto"
					>
						{Object.entries(propertyToTitleMapping)
							.filter(([key]) => {
								if (key === 'name') return false;
								if (!filterss || !filterss[addConditionRowIdx]) return false;
								
								const value = filterss[addConditionRowIdx][key];
								
								// Check if it's a default value (not changed)
								if (key === 'minMaxEv' && Array.isArray(value) && ((value[0] === -9999999 || value[0] === null || value[0] === 0) && (value[1] === 9999999 || value[1] === null || value[1] === 1000))) return true;
								if (key === 'minMaxOdds' && Array.isArray(value) && ((value[0] === -9999999 || value[0] === null) && (value[1] === 9999999 || value[1] === null))) return true;
								if (key === 'limit' && (!value || value === null || value === '')) return true;
								if (key === 'hide_alternates' && !value) return true;
								// marketDepth always shows in main list, so don't show it in add condition menu
								if (key === 'marketDepth') return false;
								if (key === 'date' && !value) return true;
								
								// For arrays, check if empty
								if (Array.isArray(value) && value.length === 0) return true;
								
								// For other types, check if not set
								if (typeof value !== 'object' && !value) return true;
								
								// If it's set to a non-default value, don't show it
								return false;
							})
							.map(([key, label], index, array) => (
								<div key={key}>
									<div
										onClick={() => {
											setPreviousView('addCondition');
											setFilterView(key);
											setActiveRowIdx(addConditionRowIdx);
											setIsAddConditionView(false);
										}}
										style={{ borderBottom: 'none' }}
										className="flex justify-between items-center py-4 cursor-pointer hover:bg-[#2a2a2a]"
									>
										<div>
											<p>{label}</p>
										</div>
										<div className="flex items-center gap-2">
											<span className="text-gray-400">&gt;</span>
										</div>
									</div>
									{index !== array.length - 1 && (
										<div style={{ height: 1, border: '1px solid white', marginTop: 0, opacity: 0.4 }} />
									)}
								</div>
							))}
					</div>
				)}

				{isRemoveConditionView && addConditionRowIdx !== null && !currentFilterView && (
					<div
						className="flex-1 overflow-y-auto"
					>
						{filterss && filterss[addConditionRowIdx] && Object.entries(filterss[addConditionRowIdx])
							.filter(([key, value]) => {
								if (key === 'name' || key === 'dateDifference' || key === 'isOver') return false;
								if (Array.isArray(value) && value.length === 0) return false;
								if (key === 'minMaxEv' && ((value[0] === -9999999 || value[0] === null || value[0] === 0) && (value[1] === 9999999 || value[1] === null || value[1] === 1000))) return false;
								if (key === 'minMaxOdds' && ((value[0] === -9999999 || value[0] === null) && (value[1] === 9999999 || value[1] === null))) return false;
								if (key === 'limit' && !value) return false;
								if (key === 'hide_alternates' && !value) return false;
								if (key === 'marketDepth' && value === 3) return false;
								if (key === 'date' && !value) return false;
								return true;
							})
							.map(([key, value], index) => (
								<div key={key}>
									<div
										className="flex justify-between items-center py-4 cursor-pointer hover:bg-[#2a2a2a]"
									>
										<div>
											<p>{propertyToTitleMapping[key] || key}</p>
										</div>
										<button
											onClick={() => {
												handleRemoveCondition(addConditionRowIdx, key);
											}}
											className="bg-red-600 hover:bg-red-700 text-white text-sm px-3 py-1 rounded"
										>
											Remove
										</button>
									</div>
									{index !== (filterss && filterss[addConditionRowIdx] ? Object.entries(filterss[addConditionRowIdx])
										.filter(([key, value]) => {
											if (key === 'name' || key === 'dateDifference' || key === 'isOver') return false;
											if (Array.isArray(value) && value.length === 0) return false;
											if (key === 'minMaxEv' && value[0] === 0 && value[1] === 1000) return false;
											if (key === 'minMaxOdds' && value[0] === -100000 && value[1] === 100000) return false;
											if (key === 'limit' && !value) return false;
											if (key === 'hide_alternates' && !value) return false;
											if (key === 'marketDepth' && value === 3) return false;
											if (key === 'date' && !value) return false;
											return true;
										}) : []).length - 1 && (
										<div style={{ height: 1, border: '1px solid white', marginTop: 0, opacity: 0.4 }} />
									)}
								</div>
							))}
					</div>
				)}
			</div>

			{/* Confirmation modal for removing conditions */}
			{confirmRemoveModal.open && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
					<div className="bg-gray-800 rounded-lg p-6 max-w-sm w-full mx-4">
						<h3 className="text-lg font-semibold mb-4">Remove Condition</h3>
						<p className="text-gray-300 mb-6">
							Are you sure you want to remove {propertyToTitleMapping[confirmRemoveModal.key] || confirmRemoveModal.key}?
						</p>
						<div className="flex gap-3">
							<button
								onClick={() => setConfirmRemoveModal({ open: false, rowIdx: null, key: null })}
								className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded text-sm"
							>
								Cancel
							</button>
							<button
								onClick={() => {
									handleRemoveCondition(confirmRemoveModal.rowIdx, confirmRemoveModal.key);
								}}
								className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded text-sm"
							>
								Remove
							</button>
						</div>
					</div>
				</div>
			)}
		</div>,
		document.body
	);
};

export const MobileOutliersFilters = (props) => {
	const {
		filters,
		setFilters,
		setIsFiltersOpen,
		email,
		queries,
		setQueries,
		user,
		setUser,
	} = props;

	const [isFiltersBarOpen, setIsFiltersBarOpen] = useState(true); // Always open when component is rendered
	const [filterView, setFilterView] = useState(null);
	const [activeRowIdx, setActiveRowIdx] = useState(null);

	// Handle filter view changes
	useEffect(() => {
		if (filterView && activeRowIdx !== null) {
			setIsFiltersBarOpen(true);
		}
	}, [filterView, activeRowIdx]);

	return (
		<>
			{isFiltersBarOpen && (
				<MobileOutliersFiltersBar
					onClose={() => {
						setIsFiltersBarOpen(false);
						setFilterView(null);
						setActiveRowIdx(null);
						setIsFiltersOpen(false); // Close the main filters
					}}
					setFilterView={setFilterView}
					setActiveRowIdx={setActiveRowIdx}
					filterss={filters}
					setFiltersBarOpen={setIsFiltersBarOpen}
					setFilters={setFilters}
					queries={queries}
					setUser={setUser}
					user={user}
					setQueries={setQueries}
					setNewQueryName={() => {}}
					newQueryNameRef={null}
					email={email}
					currentFilterView={filterView}
					currentActiveRowIdx={activeRowIdx}
				/>
			)}
		</>
	);
};
